import { Plan } from '../../../types/plans';
import axios from 'axios';

/**
 * Fetches plans.
 * @param {LoggedInNextPageContext} context - The context object.
 * @param {Object} options - The options object.
 * @param {string} options.program - The program to fetch plans for.
 * @param {string[]} options.planLengths - The plan lengths to fetch.
 * @param {string} options.dealType - The deal type to fetch plans for.
 * @param {string} options.dealCode - The deal code to fetch plans for.
 * @returns {Promise<Plan[]>} A promise that resolves to an array of plans.
 */
export const fetchPlans = async (
  {
    program,
    planLengths,
    productIDs,
    dealType,
    dealCode,
    planVersion,
  }: {
    program?: string;
    planLengths?: string[] | number[];
    productIDs?: number[];
    dealType?: string;
    dealCode?: string;
    planVersion?: string;
  },
  hostname = '',
): Promise<Plan[]> => {
  const url = new URL('http://localhost:3000');

  url.pathname = '/v0/plans';

  if (program) url.searchParams.set('program', program);
  if (dealType) url.searchParams.set('dealType', dealType);
  if (dealCode) url.searchParams.set('dealCode', dealCode);
  if (planVersion) url.searchParams.set('planVersion', planVersion);

  planLengths?.forEach((planLength) =>
    url.searchParams.append('planLengths[]', planLength.toString()),
  );

  productIDs?.forEach((productID) =>
    url.searchParams.append('productIDs[]', productID?.toString()),
  );

  const {
    data: { plans },
  } = await axios.get<{ plans: Plan[] }>(hostname + url.pathname + url.search);

  return plans;
};

/**
 * Extracts URL parameters from a given pathname.
 * @param {string} pathname - The URL pathname to extract parameters from.
 * @returns {Object} An object containing the extracted parameters.
 */
export function getUrlParams(pathname: string) {
  const isDeal =
    pathname.startsWith('/deal') || pathname.startsWith('/schooldeal');

  if (isDeal) {
    const [dealType, dealCode, program] = pathname.slice(1).split('/');
    return { dealType, dealCode, program: program };
  }

  const [, program] = pathname.slice(1).split('/');
  return { program };
}
